import React from 'react';

import { ApolloClient, NormalizedCacheObject } from '@apollo/client';

import { HreflangMeta, UrlType } from '@nxte-nl/metadata-helmet';

import { ITEMS_PER_PAGE } from '../../constants/nextConstants';
import { getBlocksQueue } from '../../queries/getBlocksQueue.gql';
import { getGiftcardsCategories_v1 } from '../../queries/getGiftcardsCategories_v1.gql';
import { getPopularSearchResultsV1 } from '../../queries/getPopularSearchResultsV1.gql';
import { getProducts } from '../../queries/getProducts.gql';
import {
  getProductsAvailableFilters,
} from '../../queries/getProductsAvailableFilters.gql';
import { getProductsList } from '../../queries/getProductsList.gql';
import { getStoreData_v1 } from '../../queries/getStoreData_v1.gql';
import { FeatureToggleValues } from '../../types/FeatureToggles';
import { PageEntity } from '../../types/Messages';
import { BaseResponse } from '../../types/apiCompoundCommon';
import { ProductsResponse, TopBlock } from '../../types/apiCompoundProduct';
import {
  GiftcardCategory, HreflangTag_v1, LanguageLink,
  PopularSearchResults,
  StoreData,
} from '../../types/apiTypes';
import { ClientLanguageLink } from '../../types/common';
import { featureToggleUtils } from '../featureToggles/featureToggles';

export type AppData = {
  categories: GiftcardCategory[];
  searchResults: PopularSearchResults;
  storeData: StoreData;
};

export type GetCOPFetchParams = {
  categoryId: string;
  client: ApolloClient<unknown>;
  filterByPage: {
    fieldName: string;
    pageId: string;
    pageType: string;
  };
  page?: string;
  topProductsQueueId: string | null;
};

export type Data =
  | PageEntity
  | TopBlock[]
  | ProductsResponse
  | GiftcardCategory[]
  | PopularSearchResults
  | StoreData
  | FeatureToggleValues
  | ClientLanguageLink[]
  | null;
export type SetData = (data: Data) => void;

export type EntityData = {
  entity: PageEntity;
  hreflangTagsMapped: HreflangMeta[] | null;
  languageLinks: ClientLanguageLink[];
};

export const serverUtils = {
  isServerSide(): boolean {
    return typeof window === 'undefined';
  },

  isNextBuild(): boolean {
    return BUILD_VARIANT === 'next';
  },

  getFeatureToggles(): Promise<FeatureToggleValues> {
    return featureToggleUtils.getFeatureTogglesFromStoryblok('');
  },

  async appDataFetch(client: ApolloClient<NormalizedCacheObject>): Promise<AppData> {
    const [categoriesResponse, searchResultsResponse, storeDataResponse] = await Promise.all([
      client.query({
        query: getGiftcardsCategories_v1,
      }),
      client.query({
        query: getPopularSearchResultsV1,
      }),
      client.query({
        query: getStoreData_v1,
      }),
    ]);

    const categories = categoriesResponse?.data?.data.body;
    const searchResults = searchResultsResponse?.data?.data.body ?? null;
    const storeData = storeDataResponse?.data?.data.body;
    return {
      categories: categories || [],
      searchResults: searchResults ?? null,
      storeData: storeData ?? null,
    };
  },

  async getProductsFetch(client: ApolloClient<unknown>, categoryId?: string) {
    const productsResponse = await client.query({
      query: getProducts,
      variables: {
        filters: {
          filterByProductType: ['GIFTCARD'],
          filterByTaxonomy: categoryId
            ? [
              {
                vocabularyID: CONFIG.vocabularyIdGiftcardCategories,
                terms: [categoryId],
              },
            ]
            : null,
        },
        paging: {
          itemsPerPage: 500,
          pageNumber: 1,
        },
      },
    });
    const { products } = productsResponse.data.data;
    return products;
  },

  async getCOPFetch({
    client,
    categoryId,
    topProductsQueueId,
    page,
    filterByPage,
  }: GetCOPFetchParams) {
    const currentPage = Number(page) || 1;
    const [blocksQueueResponse, productsResponse] = await Promise.all([
      topProductsQueueId
        ? client.query({
          query: getBlocksQueue,
          variables: {
            queueID: topProductsQueueId,
            filters: {
              filterByTaxonomy: [
                {
                  vocabularyID: CONFIG.vocabularyIdGiftcardCategories,
                  terms: [categoryId],
                },
              ],
            },
          },
        })
        : null,
      client.query({
        query: getProductsList,
        variables: {
          filters: {
            filterByProductType: ['GIFTCARD'],
            filterByTaxonomy: [
              {
                vocabularyID: CONFIG.vocabularyIdGiftcardCategories,
                terms: [categoryId],
              },
            ],
            filterByPage,
          },
          paging: {
            itemsPerPage: ITEMS_PER_PAGE * currentPage,
            pageNumber: 1,
          },
        },
      }),
    ]);
    return {
      blocksQueueResponse,
      productsResponse,
    };
  },

  async getPOPFetch(
    client: ApolloClient<unknown>,
    topProductsQueueId_v1: string | null,
    page?: string,
  ) {
    const currentPage = Number(page) || 1;
    const [filtersResponse, blocksQueueResponse, productsResponse] = await Promise.all([
      client.query({
        query: getProductsAvailableFilters,
        variables: {
          paging: { pageNumber: 1 },
          filters: {
            filterByProductType: ['GIFTCARD'],
          },
        },
      }),
      topProductsQueueId_v1
        ? client.query({
          query: getBlocksQueue,
          variables: {
            queueID: topProductsQueueId_v1,
            filters: {
              filterByTaxonomy: null,
            },
          },
        })
        : null,
      client.query({
        query: getProducts,
        variables: {
          filters: {
            filterByProductType: ['GIFTCARD'],
          },
          paging: {
            itemsPerPage: ITEMS_PER_PAGE * currentPage,
            pageNumber: 1,
          },
        },
      }),
    ]);

    return {
      filtersResponse,
      blocksQueueResponse,
      productsResponse,
    };
  },

  useSetPageData(dataItems: [Data, SetData][]) {
    const isDataAppliedRef = React.useRef(false);

    if (!isDataAppliedRef.current) {
      dataItems.forEach(([data, setData]) => setData(data));
      isDataAppliedRef.current = true;
    }
  },

  buildPathFromSlug: (slug?: string[] | string) => {
    if (!slug) {
      return '/';
    }
    // first and last params are added for adding slash in the start and in the end
    return ['', ...slug, ''].join('/');
  },
  buildResolvePath: (pathName: string, slug?: string | string[], id?: string) => {
    // Ex of tech segment: [layout]/cb/nl-nl
    const techSegment = new RegExp(/^\/\[\blayout\b\]\/[A-Za-z]{2,3}\/[A-Za-z]{2}-[A-Za-z]{2}/);
    const addSlashes = new RegExp(/^\/?|\/?$/g);
    const slugReplace = new RegExp(/\/\[\[...slug\]\]|\/\[slug\]/g);

    let path = pathName.replace(techSegment, '');

    if (slug && Array.isArray(slug)) {
      const slugPath = slug.join('/');

      path = path.replace('[[...slug]]', slugPath);
    }

    if (typeof slug === 'string') {
      path = path.replace('[slug]', slug);
    }

    if (typeof id === 'string') {
      path = path.replace('[id]', id);
    }

    return path.replace(slugReplace, '').replace(addSlashes, '/');
  },
  useLanguageTags(tags: HreflangTag_v1[] | undefined): HreflangMeta[] | null {
    return tags
      ? tags?.map(tag => {
        return {
          ...tag,
          langCode: `${tag.langCode}-${tag.countryCode.toLowerCase()}`,
          path: {
            href: tag.isDefault
              ? `${tag.origin}${tag.path}`
              : `${tag.origin}/${tag.langCode}${tag.path}`,
            type: UrlType.ABSOLUTE,
          },
        };
      })
      : null;
  },
  useLanguageUrl(languageLinks: LanguageLink[]): ClientLanguageLink[] {
    return languageLinks?.length > 0 ? languageLinks.map(link => ({
      countryCode: link.countryCode,
      langCode: link.langCode,
      url: `${link.origin}${link.isDefault ? '' : `/${link.langCode}`}${link.path}`,
    })) : [];
  },
  useEntityData(entityResponse: BaseResponse<PageEntity>): EntityData {
    const hreflangTagsMapped = serverUtils.useLanguageTags(
      entityResponse.data.data.metaData?.hreflangTags_v1,
    );
    const languageLinks = serverUtils.useLanguageUrl(entityResponse.data.data.languageLinks);

    delete (entityResponse as any).data.data.metaData.hreflangTags_v1;
    delete (entityResponse as any).data.data.languageLinks;
    return { entity: entityResponse.data, hreflangTagsMapped, languageLinks };
  },
};
